// ============================================================================
// OMS test-scenario API — builds a real order for one of the main cases
// (oms/docs/main-cases.md). Backed by /apiV1/oms/test-scenarios/, which is
// flag-gated server-side and only enabled on the OMS test environment.
// ============================================================================
import axios from "axios";
import { errorActions } from "../../../helpers/auth_helper";

export interface TestScenario {
  key: string;
  label: string;
  main_case: string;
  description: string;
}

export interface TestProduct {
  id: number;
  sku: string;
  name: string;
  zoho_item_id: number | null;
  linked_to_zoho: boolean;
}

export interface DeliveryLocation {
  key: string;
  label: string;
  pincode: string;
  main_case: string;
  description: string;
}

export interface KeyLabel {
  key: string;
  label: string;
  description?: string;
}

// Everything the free-hand "OMS Detail Test" builder needs. Vendors + warehouses are NOT carried
// here — the builder loads them live from the same source the cart-availability dashboard uses
// (GET /vendor/ and GET /vendor/{id}). `md_warehouse_ids` lets the UI show the inferred route (an
// MD warehouse ⇒ stock/transfer, any other ⇒ backorder/dropship).
export interface DetailOptions {
  payment_terms: KeyLabel[];
  modes: KeyLabel[];
  charge_types: KeyLabel[];
  md_warehouse_ids: number[];
  default_line: { vendor_id: number; warehouse_id: number; lead_time: number };
}

export interface TestScenarioOptions {
  enabled: boolean;
  scenarios: TestScenario[];
  delivery_locations: DeliveryLocation[];
  products: TestProduct[];
  outbox_backend?: string;
  detail?: DetailOptions;
}

export interface ScenarioStage {
  id: number;
  type: string;
  state: string;
  error: string | null;
  hold_reason: string | null;
}

export interface ScenarioAllocation {
  id: number;
  shipment_type: string;
  stages: ScenarioStage[];
}

export interface ScenarioDocument {
  id: number;
  type: string;
  state: string;
  zoho_doc_id: string | null;
}

export interface ScenarioResult {
  estimate_id: number;
  ship_pincode: string | null;
  sales_order_id: number;
  status: string;
  mode: string;
  payment_terms: string;
  zoho_salesorder_id: string | null;
  payment: {
    reference: string;
    amount_rupees: string;
    zoho_payment_id: string | null;
  };
  notes: string[];
  allocations: ScenarioAllocation[];
  documents: ScenarioDocument[];
}

export async function getTestScenarioOptions(): Promise<
  TestScenarioOptions | undefined
> {
  try {
    return await axios
      .get("/oms/test-scenarios/")
      .then((res) => res?.data)
      .catch((error) => {
        errorActions(error, "Error loading OMS test scenarios");
        return undefined;
      });
  } catch {
    return undefined;
  }
}

export interface RunScenarioPayload {
  scenario: string;
  delivery_key?: string;
  variant_id?: number;
  qty?: number | string;
}

// Deliberately rethrows: the caller shows the server's reason verbatim, since the
// whole point of this screen is to see exactly why a scenario failed.
export async function runTestScenario(
  payload: RunScenarioPayload
): Promise<ScenarioResult> {
  const res = await axios.post("/oms/test-scenarios/run/", payload);
  return res?.data;
}

// --- free-hand "OMS Detail Test" (multi-line estimate builder) ---------------
// One line = one variant sourced from a vendor + warehouse (exactly the cart-availability
// dashboard's choice). The route is inferred server-side from the warehouse — there is no route
// input. An MD warehouse can be sourced without a vendor; a supplier warehouse needs its vendor.
export interface DetailLineInput {
  variant_id?: number;
  sku?: string;
  qty?: number | string;
  vendor_id?: number | null;
  warehouse_id?: number;
  lead_time?: number;
  // Partial availability: only this much is in stock now — confirmed from the line's sourcing at
  // placement, the rest left pending on the availability desk. Needs a lead_time. Omit for none.
  available_qty?: number;
}

export interface DetailChargeInput {
  charge_type: string;
  amount: number | string;
  tax_value?: number | string;
  tax_type?: string;
  remark?: string;
}

export interface DetailPaymentInput {
  amount_rupees: number | string;
  push_to_zoho?: boolean;
}

export interface RunDetailPayload {
  items: DetailLineInput[];
  additional_charges?: DetailChargeInput[];
  payments?: DetailPaymentInput[];
  config?: {
    mode?: string;
    payment_terms?: string;
    auto_pay_balance?: boolean;
    delivery_key?: string;
    customer_pincode?: string;
    // ISO 8601; a naive value is read as IST. Stamped on the estimate only — every OMS clock reads
    // order placement from there.
    order_placed_time?: string;
  };
}

export interface DetailPaymentReceipt {
  reference: string;
  amount_rupees: string;
  zoho_payment_id: string | null;
}

// Same summary shape as a scenario run, but the payment block carries the whole
// ledger (payable / paid / every receipt) instead of a single advance.
export interface DetailResult extends Omit<ScenarioResult, "payment"> {
  payment: {
    payable_rupees: string;
    paid_rupees: string;
    receipts: DetailPaymentReceipt[];
  };
  line_count: number;
  charge_count: number;
}

// Rethrows for the same reason as runTestScenario — the caller shows the server's
// reason verbatim (admission gate / Zoho error).
export async function runDetailTest(
  payload: RunDetailPayload
): Promise<DetailResult> {
  const res = await axios.post("/oms/test-scenarios/detail/", payload);
  return res?.data;
}

export interface PaymentTransaction {
  id: number;
  payment_id: string | null;
  status: string | null;
  amount_rupees: string;
  method: string | null;
  created_at: string | null;
}

export interface PaymentState {
  estimate_id: number;
  client: string;
  payable_rupees: string;
  paid_rupees: string;
  balance_rupees: string;
  fully_paid: boolean;
  sales_order_id: number | null;
  payment_terms: string | null;
  // null when the estimate has no order yet — "not applicable", not "blocked".
  cleared_to_ship: boolean | null;
  zoho_reachable: boolean;
  transactions: PaymentTransaction[];
}

export interface CollectPaymentResult {
  payment: {
    reference: string;
    amount_rupees: string;
    zoho_payment_id: string | null;
  };
  before: PaymentState;
  state: PaymentState;
}

export async function getPaymentState(
  estimateId: number | string
): Promise<PaymentState> {
  const res = await axios.get("/oms/test-scenarios/payment/", {
    params: { estimate_id: estimateId },
  });
  return res?.data;
}

// Rethrows for the same reason as runTestScenario — and here the server's error may
// carry a `warning` the tester must see before retrying.
export async function collectPayment(payload: {
  estimate_id: number | string;
  amount_rupees?: number | string;
}): Promise<CollectPaymentResult> {
  const res = await axios.post("/oms/test-scenarios/payment/", payload);
  return res?.data;
}
